import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// @types
import { StaffOtpChallenge } from '../../../@types/auth';

// ----------------------------------------------------------------------
// Staff sign-in code (auth/staff-otp), step 3 of the login form. After the
// password (and the authenticator code, if the account has one) the API has
// sent a 6-digit code by email or WhatsApp; verifying it opens the session.
//
// The email + password stay in the parent's component state only (never
// storage, never the URL) so "Send a new code" can call sign-in again —
// that is how the API resends. The code and the password are never logged.
// ----------------------------------------------------------------------

export const RESEND_SECONDS = 30;

// Arabic-Indic (٠-٩) and Eastern Arabic-Indic (۰-۹) digits → 0-9, then keep digits only.
export function normalizeCode(raw: string): string {
  return raw
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/\D/g, '')
    .slice(0, 6);
}

type Props = {
  email: string;
  password: string;
  challenge: StaffOtpChallenge;
  /** Sign-in asked for the authenticator code again (account with TOTP). */
  onMfaRequired: () => void;
  onBack: () => void;
};

export default function StaffCodeStep({
  email,
  password,
  challenge: firstChallenge,
  onMfaRequired,
  onBack,
}: Props) {
  const { login, verifyStaffCode } = useAuth();
  const navigate = useNavigate();

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [challenge, setChallenge] = useState<StaffOtpChallenge>(firstChallenge);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [needNewCode, setNeedNewCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const isWhatsApp = challenge.channel === 'whatsapp';
  const destination = isWhatsApp ? 'to your WhatsApp' : `to ${challenge.sentTo || 'your email'}`;

  const sendNewCode = async () => {
    setSending(true);
    setError(null);
    setInfo(null);
    try {
      const result = await login(email, password);
      if (!mounted.current) return;
      if (result.otpRequired) {
        setChallenge(result);
        setCode('');
        setNeedNewCode(false);
        setCooldown(RESEND_SECONDS);
        if (result.resent) {
          setInfo(
            result.channel === 'whatsapp'
              ? 'We sent a new code to your WhatsApp.'
              : `We sent a new code to ${result.sentTo || 'your email'}.`,
          );
        } else {
          setInfo(
            result.channel === 'whatsapp'
              ? 'A code was just sent; check your WhatsApp.'
              : 'A code was just sent; check your inbox.',
          );
        }
      } else if (result.mfaRequired) {
        onMfaRequired();
      }
    } catch (err: any) {
      if (mounted.current) {
        setError(err?.message || 'Could not send a new code. Try again.');
      }
    } finally {
      if (mounted.current) setSending(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (verifying || needNewCode) return;
    const otp = normalizeCode(code);
    if (otp.length !== 6) {
      setError('Enter the 6-digit code.');
      return;
    }
    setVerifying(true);
    setError(null);
    setInfo(null);
    try {
      const result = await verifyStaffCode(email, otp);
      if (result.ok) {
        // Signed in. A new account must choose its own password first; the
        // guards also enforce this, the navigate just gets there directly.
        if (result.forcePasswordChange) {
          navigate(PATH_AUTH.changePassword, { replace: true });
        }
        return;
      }
      if (!mounted.current) return;
      setError(result.message);
      setCode('');
      if (result.reason === 'expired' || result.reason === 'too_many') {
        setNeedNewCode(true);
      }
    } catch (err: any) {
      if (mounted.current) {
        setError(err?.message || 'Could not check the code. Try again.');
      }
    } finally {
      if (mounted.current) setVerifying(false);
    }
  };

  return (
    <form noValidate onSubmit={onSubmit} data-testid="staff-code-step">
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle1">Enter your sign-in code</Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
            data-testid="staff-code-sent-to"
          >
            We sent a 6-digit code {destination}.
          </Typography>
          {challenge.activation && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
              data-testid="staff-code-activation"
            >
              This is your first sign-in: the code activates your account.
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" data-testid="staff-code-error">
            {error}
          </Alert>
        )}
        {info && (
          <Alert severity="info" data-testid="staff-code-info">
            {info}
          </Alert>
        )}

        {needNewCode ? (
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            loading={sending}
            onClick={sendNewCode}
            data-testid="staff-code-get-new"
          >
            Get a new code
          </LoadingButton>
        ) : (
          <>
            <TextField
              fullWidth
              autoFocus
              label="Sign-in code"
              value={code}
              onChange={(e) => setCode(normalizeCode(e.target.value))}
              inputProps={{
                inputMode: 'numeric',
                autoComplete: 'one-time-code',
                maxLength: 6,
                'data-testid': 'staff-code-input',
              }}
            />
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={verifying}
              data-testid="staff-code-verify"
            >
              Verify
            </LoadingButton>
          </>
        )}

        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Button onClick={onBack} disabled={verifying} data-testid="staff-code-back">
            Back
          </Button>
          {!needNewCode && (
            <Button
              onClick={sendNewCode}
              disabled={cooldown > 0 || sending || verifying}
              data-testid="staff-code-resend"
            >
              {cooldown > 0 ? `Send a new code (${cooldown}s)` : 'Send a new code'}
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );
}
